<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-header">
        <h1 class="auth-title">비밀번호 찾기</h1>
        <p class="auth-subtitle">가입한 이메일로 재설정 링크를 보내드립니다</p>
      </div>

      <form v-if="!isSent" @submit.prevent="handleResetPassword" class="auth-form">
        <div class="input-group">
          <label for="email">이메일</label>
          <input 
            type="email" 
            id="email" 
            v-model="email" 
            placeholder="가입한 이메일을 입력하세요"
            required
          />
        </div>

        <p v-if="error" class="error-message">{{ error }}</p>

        <button type="submit" class="btn-primary" :disabled="isLoading">
          {{ isLoading ? '발송 중...' : '재설정 링크 보내기' }}
        </button>
      </form>

      <div v-else class="success-message">
        <div class="success-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3182f6" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h2 class="success-title">이메일을 확인해주세요</h2>
        <p class="success-desc">
          <strong>{{ email }}</strong>으로<br>
          비밀번호 재설정 링크를 보냈습니다.
        </p>
        <button @click="isSent = false" class="btn-secondary">
          다른 이메일로 다시 보내기
        </button>
      </div>

      <p class="auth-link">
        <router-link to="/login">로그인으로 돌아가기</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'ForgotPasswordView',
  setup() {
    const { resetPassword, error } = useAuth()
    
    const email = ref('')
    const isLoading = ref(false)
    const isSent = ref(false)

    const handleResetPassword = async () => {
      isLoading.value = true
      try {
        await resetPassword(email.value)
        isSent.value = true
      } catch (e) {
        // 에러는 useAuth에서 처리
      } finally {
        isLoading.value = false
      }
    }

    return {
      email,
      error,
      isLoading,
      isSent,
      handleResetPassword
    }
  }
}
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.auth-container {
  width: 100%;
  max-width: 400px;
  background: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.auth-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.auth-subtitle {
  font-size: 15px;
  color: #6b7684;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.input-group input {
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.15s;
}

.input-group input:focus {
  outline: none;
  border-color: #3182f6;
  box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.1);
}

.input-group input::placeholder {
  color: #adb5bd;
}

.error-message {
  color: #e03131;
  font-size: 14px;
  text-align: center;
  margin: 0;
}

.btn-primary {
  padding: 16px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: #1b64da;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  width: 100%;
  padding: 14px;
  background: #f2f4f6;
  color: #333;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover {
  background: #e5e8eb;
}

.success-message {
  text-align: center;
}

.success-icon {
  margin-bottom: 20px;
}

.success-title {
  font-size: 20px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 12px;
}

.success-desc {
  font-size: 15px;
  color: #6b7684;
  line-height: 1.6;
  margin-bottom: 24px;
}

.success-desc strong {
  color: #191f28;
}

.auth-link {
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: #6b7684;
}

.auth-link a {
  color: #3182f6;
  font-weight: 600;
}

.auth-link a:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .auth-container {
    padding: 32px 24px;
  }

  .auth-title {
    font-size: 24px;
  }
}
</style>